import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  ArrowLeft,
  Users,
  Globe,
  Eye,
  EyeOff,
  ChevronDown,
  Shield,
  School,
  GraduationCap,
  User,
  HeartHandshake,
} from "lucide-react";

// ✅ COMMENTED: i18n disabled (English only for now)
// import { useTranslation } from "react-i18next";

import { useAuth } from "../../contexts/AuthContext";
import { getCurrentUser } from "../../modules/platform/api/users";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { AuthSplitLayout } from "../../components/auth/AuthSplitLayout";
import { handleApiError, showSuccessToast } from "../../utils/errorHandler";
import { ROUTES } from "../../utils/constants";
import { validateEmail } from "../../utils/validation";

// ✅ COMMENTED: Language switch disabled (English only for now)
// import { LanguageSwitch } from "../../components/ui/LanguageSwitch";

interface LoginFormData {
  email: string;
  password: string;
}

type RoleKey = "dev_admin" | "school_admin" | "teacher" | "student" | "parent" | "";
type ModeKey = "admin" | "user";

const initialTouched = { email: false, password: false };

const TEXT = {
  home: "Home",
  title: "Login",
  subtitle: "Enter your email and password to continue.",
  emailRequired: "Email is required",
  invalidEmail: "Please enter a valid email",
  passwordRequired: "Password is required",
  forgot: "Forgot password?",
  start: "Sign in",
  entering: "Signing in...",
  welcome: "Welcome back",
  comingSoon: "Coming soon",
  google: "Continue with Google",
  sso: "Continue with SSO",
  soonNote: "More login options will be available soon.",
  noAccount: "Don't have an account?",
  createAccount: "Create account",
  emailLabel: "Email",
  emailPlaceholder: "Enter your email",
  passwordLabel: "Password",
  passwordPlaceholder: "Enter your password",
  showPassword: "Show password",
  hidePassword: "Hide password",
  selectRole: "Select Login Type",
  selected: "Selected",
};

export const LoginPage: React.FC = () => {
  // ✅ COMMENTED: i18n disabled (English only for now)
  // const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState(initialTouched);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // ✅ Role selection UI state — restore from localStorage if present
  const [selectedRole, setSelectedRole] = useState<RoleKey>(() => {
    try {
      const v = localStorage.getItem('tkm_selected_role');
      return (v as RoleKey) ?? '';
    } catch {
      return '';
    }
  });

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ✅ Decide which dropdown list to show using query param:
  // /login?mode=admin  -> School Admin + Dev Team
  // /login?mode=user   -> Parent + Student + Teacher
  const mode: ModeKey = (searchParams.get("mode") === "user" ? "user" : "admin") as ModeKey;

  // ✅ Invite redirect (unchanged)
  useEffect(() => {
    const inviteToken = searchParams.get("invite_token") || searchParams.get("token");
    const tenantId = searchParams.get("tenant_id");
    if (inviteToken) {
      const params = new URLSearchParams();
      params.set("token", inviteToken);
      if (tenantId) params.set("tenant_id", tenantId);
      navigate(`${ROUTES.INVITE_ACCEPT}?${params.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // ✅ All roles (master list)
  const ALL_ROLE_OPTIONS = useMemo(
    () =>
      [
        { key: "school_admin" as const, label: "School Admin Login", icon: School, accent: "from-emerald-500 to-sky-500" },
        { key: "dev_admin" as const, label: "Dev Team Login", icon: Shield, accent: "from-fuchsia-500 to-sky-500" },

        { key: "parent" as const, label: "Parent", icon: HeartHandshake, accent: "from-rose-500 to-pink-500" },
        { key: "student" as const, label: "Student", icon: User, accent: "from-sky-500 to-indigo-500" },
        { key: "teacher" as const, label: "Teacher", icon: GraduationCap, accent: "from-amber-500 to-orange-500" },
      ] as const,
    []
  );

  // ✅ Filter dropdown options by mode
  const ROLE_OPTIONS = useMemo(() => {
    if (mode === "admin") return ALL_ROLE_OPTIONS.filter((r) => r.key === "school_admin" || r.key === "dev_admin");
    return ALL_ROLE_OPTIONS.filter((r) => r.key === "parent" || r.key === "student" || r.key === "teacher");
  }, [ALL_ROLE_OPTIONS, mode]);

  // ✅ Ensure selectedRole is valid for the current mode (prevents weird dropdown like your screenshot)
  useEffect(() => {
    const allowedKeys = ROLE_OPTIONS.map((r) => r.key) as string[];
    if (!allowedKeys.includes(String(selectedRole))) {
      // Default selection per mode
      setSelectedRole(mode === "admin" ? "school_admin" : "student");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, ROLE_OPTIONS]);

  const selectedRoleObj = useMemo(
    () => ROLE_OPTIONS.find((r) => r.key === selectedRole) || ROLE_OPTIONS[0],
    [ROLE_OPTIONS, selectedRole]
  );

  const {
    register: registerField,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const emailRegister = registerField("email", {
    required: TEXT.emailRequired,
    validate: (value) => validateEmail(value) || TEXT.invalidEmail,
  });

  const passwordRegister = registerField("password", {
    required: TEXT.passwordRequired,
  });

  const showEmailError = (submitted || touched.email) && errors.email?.message;
  const showPasswordError = (submitted || touched.password) && errors.password?.message;

  // ✅ Reserve space so the page does NOT move up/down when error text appears
  const emailHelperText = showEmailError ? String(errors.email?.message) : " ";
  const passwordHelperText = showPasswordError ? String(errors.password?.message) : " ";

  // (no click-outside handler required for tabs)

  // ✅ When role changes: reset errors/touched/submitted so UX is clean
  useEffect(() => {
    setSubmitted(false);
    setTouched(initialTouched);
    setLoginError(null);
    reset(undefined, { keepValues: true });
  }, [selectedRole, reset]);

  const watchedEmail = watch("email");
  const watchedPassword = watch("password");
  useEffect(() => {
    setLoginError(null);
  }, [watchedEmail, watchedPassword]);

  const onSubmit = async (data: LoginFormData) => {
    setSubmitted(true);
    setIsLoading(true);
    try {
      await login(data.email, data.password);

      // After successful login, fetch authoritative user role from server
      let actualRoleLabel = '';
      try {
        const me = await getCurrentUser();
        const primary = me.user.roles?.[0];
        const roleMap: Record<string, string> = {
          DEV_TEAM: 'Dev Team',
          SCHOOL_ADMIN: 'School Admin',
          TEACHER: 'Teacher',
          PARENT: 'Parent',
          STUDENT: 'Student',
        };
        if (primary && roleMap[primary]) {
          actualRoleLabel = roleMap[primary];
          // overwrite stored selected role to avoid stale values
          try {
            localStorage.setItem('tkm_selected_role', primary as any);
          } catch {}
        } else {
          // unknown role — remove stale selection
          try {
            localStorage.removeItem('tkm_selected_role');
          } catch {}
        }
      } catch (err) {
        // fallback: keep existing selectedRoleObj.label but don't rely on it for correctness
        actualRoleLabel = selectedRoleObj.label;
      }

      showSuccessToast(actualRoleLabel ? `${TEXT.welcome} • ${actualRoleLabel} Login` : TEXT.welcome);

      const from = (location.state as any)?.from?.pathname || ROUTES.ADMIN_DASHBOARD;
      navigate(from, { replace: true });
    } catch (error) {
      const message = handleApiError(error);
      setLoginError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const RoleIcon = selectedRoleObj.icon;

  return (
    <AuthSplitLayout showBackButton={false}>
      <div className="space-y-6 p-4 sm:p-6 lg:p-7 max-w-md mx-auto">
        {/* ✅ COMMENTED: Language switch disabled (English only for now) */}
        {/*
        <div className="flex items-center justify-end">
          <LanguageSwitch />
        </div>
        */}

        <button
          type="button"
          onClick={() => navigate(ROUTES.LANDING)}
          className="flex items-center gap-1.5 text-base font-semibold text-amber-600 hover:text-amber-700 hover:scale-105 transition-all duration-200 -ml-2"
          aria-label={TEXT.home}
        >
          <ArrowLeft className="h-5 w-5" />
          {TEXT.home}
        </button>

        {/* Role-specific banners removed per request — keep dropdown only */}
        <h1 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white">
          {TEXT.title}
        </h1>

        {/* ✅ Role Tabs (replace dropdown). Hidden when mode=admin */}
        {mode !== 'admin' && (
          <div className="mt-4 w-full">
            <div className="w-full grid grid-cols-3 gap-3">
              {ROLE_OPTIONS.map((r) => {
                const Ico = r.icon;
                const active = selectedRole === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => {
                      setSelectedRole(r.key);
                      try {
                        localStorage.setItem('tkm_selected_role', r.key);
                      } catch {
                        // ignore
                      }
                    }}
                    className={[
                      "h-12 rounded-xl text-sm font-semibold transition-all",
                      "flex items-center justify-center gap-2",
                      active
                        ? "bg-teal-600 text-white shadow-md"
                        : "bg-white/80 dark:bg-slate-900/60 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800",
                    ].join(" ")}
                    aria-pressed={active}
                  >
                    <Ico className="h-4 w-4" />
                    <span className="truncate">{r.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          {/* Social / SSO buttons removed per request */}
          {/* 
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-2xl border-2 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 shadow-md"
              disabled
              title={TEXT.comingSoon}
            >
              <Globe className="h-5 w-5 mr-2" />
              {TEXT.google}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-2xl border-2 border-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-700 shadow-md"
              disabled
              title={TEXT.comingSoon}
            >
              <Users className="h-5 w-5 mr-2" />
              {TEXT.sso}
            </Button>

            <p className="text-center text-xs font-medium text-amber-600 bg-amber-50 px-3 py-2 rounded-xl">
              {TEXT.soonNote}
            </p>
          </div>
          */}

          <Input
            label={TEXT.emailLabel}
            type="email"
            {...emailRegister}
            onBlur={(e) => {
              emailRegister.onBlur(e);
              setTouched((prev) => ({ ...prev, email: true }));
            }}
            error={showEmailError ? errors.email?.message : undefined}
            helperText={emailHelperText}
            placeholder={TEXT.emailPlaceholder}
            className="h-14 rounded-2xl border-2 border-sky-300 focus:border-fuchsia-400 focus:ring-4 focus:ring-fuchsia-200/50 shadow-lg text-base"
          />

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-base font-semibold text-gray-800 dark:text-gray-200">
                {TEXT.passwordLabel}
              </label>

              <Link
                to={ROUTES.FORGOT_PASSWORD}
                className="text-sm font-semibold bg-gradient-to-r from-rose-400 to-pink-500 bg-clip-text text-transparent hover:scale-105 transition-all"
              >
                {TEXT.forgot}
              </Link>
            </div>

            <div className="relative">
              <Input
                id="login-password"
                type={showPassword ? "text" : "password"}
                {...passwordRegister}
                onBlur={(e) => {
                  passwordRegister.onBlur(e);
                  setTouched((prev) => ({ ...prev, password: true }));
                }}
                error={showPasswordError ? errors.password?.message : undefined}
                helperText={passwordHelperText}
                placeholder={TEXT.passwordPlaceholder}
                className="h-14 rounded-2xl border-2 border-orange-300 focus:border-rose-400 focus:ring-4 focus:ring-rose-200/50 shadow-lg text-base pr-12"
              />

              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? TEXT.hidePassword : TEXT.showPassword}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {loginError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                {loginError}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className={`w-full h-14 bg-gradient-to-r ${selectedRoleObj.accent} text-lg font-bold rounded-3xl shadow-2xl text-white`}
            isLoading={isLoading}
          >
            {isLoading ? TEXT.entering : TEXT.start}
          </Button>
        </form>

        
      </div>
    </AuthSplitLayout>
  );
};
